import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import api from "@/api";
import { EbookModal } from "./EbookModal";

const spans = [
  "md:col-span-7 aspect-[16/10]",
  "md:col-span-5 aspect-[4/3.2]",
  "md:col-span-5 aspect-[4/3.2]",
  "md:col-span-7 aspect-[16/10]",
  "md:col-span-6 aspect-[16/9]",
  "md:col-span-6 aspect-[16/9]",
];

export const Catalog = () => {
  const [ebooks, setEbooks] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/ebooks")
      .then(({ data }) => setEbooks(data))
      .catch(() => setEbooks([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="catalog" className="py-28 md:py-44" data-testid="catalog-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-24">
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="font-display uppercase leading-[0.9] text-6xl md:text-8xl"
            data-testid="catalog-heading"
          >
            The<br />
            <span className="text-stroke">Catalog</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="font-editorial italic text-lg md:text-xl text-foreground/60 max-w-sm"
          >
            Limited digital editions. Each volume, a machine brought back to life on the page.
          </motion.p>
        </div>

        {loading ? (
          <div className="h-64 flex items-center justify-center text-foreground/40 text-sm tracking-widest uppercase" data-testid="catalog-loading">
            Rolling out of the garage…
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
            {ebooks.map((book, i) => (
              <motion.button
                key={book.id}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: (i % 3) * 0.12, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => setActive(book)}
                className={`group relative text-left border border-white/20 hover:border-primary/70 transition-colors duration-500 overflow-hidden spotlight ${spans[i % spans.length]}`}
                data-testid={`ebook-card-${book.id}`}
              >
                <img
                  src={book.cover_url}
                  alt={book.title}
                  className="absolute inset-0 w-full h-full object-cover scale-100 group-hover:scale-105 transition-transform duration-700 ease-out"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                {book.featured && (
                  <span className="absolute top-4 left-4 bg-primary text-white text-[10px] font-bold tracking-[0.25em] uppercase px-3 py-1">
                    Featured
                  </span>
                )}
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 flex items-end justify-between gap-4">
                  <div>
                    <p className="text-[10px] font-semibold tracking-[0.3em] uppercase text-foreground/50 mb-2">
                      {book.category} — {book.year}
                    </p>
                    <h3 className="font-display uppercase text-2xl md:text-4xl leading-none text-white">
                      {book.title}
                    </h3>
                    <p className="font-editorial italic text-sm md:text-base text-foreground/60 mt-1">
                      {book.subtitle}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className="font-display text-xl md:text-2xl text-white">${book.price.toFixed(2)}</span>
                    <span className="w-10 h-10 border border-white/30 flex items-center justify-center group-hover:bg-primary group-hover:border-primary transition-colors duration-300">
                      <ArrowUpRight size={16} className="text-white" />
                    </span>
                  </div>
                </div>
              </motion.button>
            ))}
          </div>
        )}
      </div>
      <EbookModal ebook={active} open={!!active} onClose={() => setActive(null)} />
    </section>
  );
};
